import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { fileAPI } from '../services/api';

/**
 * Custom hook for file editing functionality
 * Handles loading, saving, and managing file state in editors
 */
export const useFileEditor = (defaultCode = '') => {
  const location = useLocation();
  const navigate = useNavigate();

  const [currentFile, setCurrentFile] = useState(location.state?.file || null);
  const [code, setCode] = useState(location.state?.file?.content || defaultCode);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Load file content when component mounts or file changes
  useEffect(() => {
    const loadFileContent = async () => {
      if (currentFile && currentFile.id) {
        try {
          const fileData = await fileAPI.getFile(currentFile.id);
          setCode(fileData.content || '');
          setCurrentFile(fileData);
          setHasUnsavedChanges(false);
        } catch (error) {
          console.error('Error loading file:', error);
        }
      }
    };

    loadFileContent();
  }, [currentFile?.id]);

  // Handle code changes
  const handleCodeChange = (value) => {
    setCode(value || '');
    if (currentFile && value !== currentFile.content) {
      setHasUnsavedChanges(true);
    }
  };

  // Save file
  const saveFile = async () => {
    if (!currentFile) {
      alert('No file is currently open. Please open a file from the dashboard.');
      return false;
    }

    try {
      await fileAPI.updateFile(currentFile.id, code);
      setHasUnsavedChanges(false);
      setCurrentFile({ ...currentFile, content: code });
      alert('File saved successfully!');
      return true;
    } catch (error) {
      console.error('Error saving file:', error);
      alert('Failed to save file');
      return false;
    }
  };

  // Go to dashboard
  const goToDashboard = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Do you want to leave without saving?')) {
        navigate('/dashboard');
      }
    } else {
      navigate('/dashboard');
    }
  };

  return {
    currentFile,
    code,
    hasUnsavedChanges,
    handleCodeChange,
    saveFile,
    goToDashboard,
    setCode, // Allow manual code setting if needed
  };
};
